import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Button from '../bootstrap/Button';
import Popovers from '../bootstrap/Popovers';
import CustomSelect from '../bootstrap/forms/CustomSelect';
import Checks from '../bootstrap/forms/Checks';

/******************************************************************
 * 파일명 : frontend/src/components/common/CalendarBox.js
 * 설명 : 관리자 화면 공용 "기간 선택 UI" 컴포넌트
 *   - CalendarPresetFilter : 달력 아이콘 + 기간 프리셋(주/월/분기/반기/년) 팝오버.
 *                            프리셋 입력값과 조회/초기화 흐름은 컴포넌트가 소유하고,
 *                            "조회 시 실제로 무엇을 할지"(onApply)만 페이지가 주입한다.
 *   - CalendarBox          : ◀ 2026.08.31 ~ 2026.09.06 ▶ 날짜 네비게이터 (껍데기).
 *                            날짜 계산·이동·조회는 전부 페이지가 콜백/ReactNode 로 주입한다.
 ******************************************************************/

/**
 * 달력 아이콘 버튼 + 그 위에 뜨는 기간 프리셋 팝오버.
 *
 * 프리셋 버튼 목록 + 프리셋별 입력(주별 체크 / 월·분기·반기·년 선택) + "조회 / 초기화" 를 모두 소유한다.
 * 페이지는 "조회" 시 실행할 onApply(filterValue, filterText) 콜백만 넘긴다.
 */
export const CalendarPresetFilter = ({
	popRef,
	isDisabled,
	options,
	excludeWeek,
	selected,
	onSelect,
	matchBy,
	yearOptions,
	onApply,
	onApplyClick,
	onReset,
	applyText,
	resetText,
	triggerClassName,
	triggerButtonProps,
	onTriggerClick,
	placement,
	popoverClassName,
}) => {
	const { t } = useTranslation();

	// 프리셋별 부가 입력값은 이 컴포넌트가 소유
	const [weekCheck, setWeekCheck] = useState(false);
	const [monthSelect, setMonthSelect] = useState(moment().month() + 1);
	const [quarterSelect, setQuarterSelect] = useState(String(Math.ceil((moment().month() + 1) / 3)));
	const [halfSelect, setHalfSelect] = useState(String(moment().month() < 6 ? 1 : 2));
	const [selectYearFilter, setSelectYearFilter] = useState(moment().format('YYYY'));

	const presetList = (options || []).filter(
		(opt) => !(excludeWeek && opt.value === 'week'),
	);

	const isSelected = (opt, index) => {
		if (matchBy === 'index') {
			return selected?.index === index;
		}
		if (matchBy === 'label') {
			return selected?.status?.label === opt.label;
		}
		return selected?.status?.value === opt.value;
	};

	// 현재 선택된 프리셋 종류 ('week' | 'month' | 'quarter' | 'half' | 'year')
	const presetValue = selected?.status?.value;

	const handleApply = () => {
		if (onApplyClick) onApplyClick(); // 프리셋 매칭 여부와 무관하게 "조회" 클릭 시 항상 실행

		if (presetValue === 'week' && weekCheck) {
			onApply('week', 'week');
		} else if (presetValue === 'month' && monthSelect) {
			onApply('month', monthSelect);
		} else if (presetValue === 'quarter' && quarterSelect) {
			onApply('quarter', quarterSelect);
		} else if (presetValue === 'half' && halfSelect) {
			onApply('half', halfSelect);
		} else if (presetValue === 'year' && selectYearFilter) {
			onApply('year', selectYearFilter);
		}

		if (popRef?.current) popRef.current.onClick(); // 팝오버 닫기
	};

	const handleReset = () => {
		setWeekCheck(false);
		setMonthSelect(moment().month() + 1);
		setHalfSelect(String(moment().month() < 6 ? 1 : 2));
		setQuarterSelect(String(Math.ceil((moment().month() + 1) / 3)));
		setSelectYearFilter(moment().format('YYYY'));
		if (onReset) {
			onReset();
		}
	};

	return (
		<Popovers
			ref={popRef}
			placement={placement}
			desc={
				<div>
					<div
						className='mx-2 py-1 d-flex'
						style={{ backgroundColor: '#F1F1F1', borderRadius: 5 }}>
						{presetList.map((opt, index) => {
							const isButtonSelected = isSelected(opt, index);
							return (
								<div
									className='px-1 text-nowrap text-truncate'
									key={`calendarPreset-${opt.value}`}>
									<Button
										className='px-1'
										size='sm'
										style={{
											borderRadius: 5,
											backgroundColor: isButtonSelected ? 'white' : '#F1F1F1',
										}}
										onClick={() => {
											onSelect({ ...selected, index, status: opt });
										}}>
										<span
											className={`${isButtonSelected ? 'fw-bold' : 'text-muted'} px-3 ${
												isButtonSelected ? 'selected' : ''
											}`}
											style={{ fontSize: 13 }}>
											{opt.label}
										</span>
									</Button>
								</div>
							);
						})}
					</div>
					<div>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								paddingTop: '20px',
							}}>
							{presetValue === 'week' && (
								<Checks
									type='check'
									label={t('restManage.viewWeek')}
									checked={weekCheck}
									onChange={(e) => {
										setWeekCheck(e.target.checked);
									}}
								/>
							)}
							{presetValue === 'month' && (
								<CustomSelect
									ariaLabel={'monthSelect'}
									className='text-center'
									list={Array.from(Array(12).keys()).map((v) => {
										return { value: v + 1, text: t('common.label.month', { num: v + 1 }) };
									})}
									placeholder={t('restManage.selectMonth')}
									value={monthSelect || ''}
									onChange={(e) => {
										setMonthSelect(parseInt(e.target.value, 10));
									}}
								/>
							)}
							{presetValue === 'quarter' && (
								<CustomSelect
									ariaLabel={'quarterSelect'}
									className='text-center'
									list={Array.from(Array(4).keys()).map((v) => {
										return { value: v + 1, text: v + 1 + t('restManage.quarter') };
									})}
									placeholder={t('restManage.selectQuarter')}
									value={quarterSelect}
									onChange={(e) => {
										setQuarterSelect(e.target.value);
									}}
								/>
							)}
							{presetValue === 'half' && (
								<CustomSelect
									ariaLabel={'halfSelect'}
									className='text-center'
									list={Array.from(Array(2).keys()).map((v) => {
										return {
											value: v + 1,
											text: v === 0 ? t('restManage.firstHalf') : t('restManage.secondHalfYear'),
										};
									})}
									placeholder={t('restManage.selectHalfYear')}
									value={halfSelect}
									onChange={(e) => {
										setHalfSelect(e.target.value);
									}}
								/>
							)}
							{presetValue === 'year' && (
								<CustomSelect
									ariaLabel={'yearSelect'}
									className='text-center'
									list={(yearOptions || []).map((year) => {
										return { value: year, label: t(year) };
									})}
									value={selectYearFilter}
									onChange={(e) => {
										setSelectYearFilter(e.target.value);
									}}
									placeholder={t('restManage.selectYear')}
								/>
							)}
							<div
								className='col-4'
								style={{ display: 'flex', justifyContent: 'space-around' }}>
								<Button
									color='info'
									className='text-nowrap'
									style={{ flexShrink: 0 }}
									onClick={handleApply}>
									{applyText || t('restManage.viewResult')}
								</Button>
								<Button
									color='success'
									className='text-nowrap'
									style={{ flexShrink: 0 }}
									onClick={handleReset}>
									{resetText || t('filters.reset')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			}
			className={popoverClassName}
			trigger='click'>
			<Button
				type='button'
				color='info'
				isLight
				icon='CalendarCheck'
				isDisable={isDisabled}
				size='sm'
				className={triggerClassName}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...triggerButtonProps}
				onClick={() => {
					if (popRef?.current) popRef.current.onOpen();
					if (onTriggerClick) onTriggerClick();
				}}
			/>
		</Popovers>
	);
};

CalendarPresetFilter.propTypes = {
	/** 프리셋 팝오버(Popovers) ref */
	// eslint-disable-next-line react/forbid-prop-types
	popRef: PropTypes.object.isRequired,
	isDisabled: PropTypes.bool,
	/** [{ value, label }] 형태의 프리셋 목록 (페이지에서 t() 적용해 전달) */
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.node,
		}),
	).isRequired,
	/** true 면 value === 'week' 항목 제거 (예: 월별 탭) */
	excludeWeek: PropTypes.bool,
	/** 현재 선택 상태 ( { index, status: { value, label } } ) */
	// eslint-disable-next-line react/forbid-prop-types
	selected: PropTypes.object,
	onSelect: PropTypes.func.isRequired,
	/** 선택 여부 판정 기준 */
	matchBy: PropTypes.oneOf(['value', 'index', 'label']),
	/** 년도 Select 목록 (회사 가입일 연도 ~ 현재 연도) */
	// eslint-disable-next-line react/forbid-prop-types
	yearOptions: PropTypes.array,
	/** "조회" 클릭 시 실행. (filterValue, filterText) => void */
	onApply: PropTypes.func.isRequired,
	/** "조회" 클릭 시 프리셋 매칭 여부와 무관하게 항상 먼저 실행할 콜백 (선택) */
	onApplyClick: PropTypes.func,
	/** "초기화" 시 드롭다운 리셋 후 추가로 실행할 콜백 (선택) */
	onReset: PropTypes.func,
	applyText: PropTypes.node,
	resetText: PropTypes.node,
	/** 달력 아이콘 Button 의 className (기본 'me-2') */
	triggerClassName: PropTypes.string,
	/** 트리거 Button 에 병합할 추가 props */
	// eslint-disable-next-line react/forbid-prop-types
	triggerButtonProps: PropTypes.object,
	/** 달력 아이콘 클릭 시 팝오버 토글과 별개로 실행할 콜백 */
	onTriggerClick: PropTypes.func,
	placement: PropTypes.string,
	popoverClassName: PropTypes.string,
};

CalendarPresetFilter.defaultProps = {
	isDisabled: false,
	excludeWeek: false,
	selected: null,
	matchBy: 'value',
	yearOptions: [],
	onApplyClick: null,
	onReset: null,
	applyText: null,
	resetText: null,
	triggerClassName: 'me-2',
	triggerButtonProps: null,
	onTriggerClick: null,
	placement: 'bottom',
	popoverClassName: 'mw-100',
};

/**
 * ◀ [날짜 라벨(클릭 시 DateRange 팝오버)] ▶ navigator.
 *
 * 날짜 범위 계산/선택 UI(DateRange, 그룹 패널, 조회 버튼 등)는 popoverDesc 로 페이지가 주입한다.
 */
export const CalendarBox = ({
	className,
	isDisabled,
	label,
	labelClassName,
	prevIcon,
	nextIcon,
	prevClassName,
	nextClassName,
	onPrev,
	onNext,
	popRef,
	popoverId,
	popoverDesc,
	popoverClassName,
	placement,
}) => (
	<div className={className}>
		<Button
			type='button'
			icon={prevIcon}
			isDisable={isDisabled}
			className={prevClassName}
			onClick={onPrev}
		/>
		<Popovers
			id={popoverId}
			placement={placement}
			ref={popRef}
			desc={popoverDesc}
			className={popoverClassName}
			trigger='click'>
			<span className={labelClassName} style={{ cursor: 'pointer' }}>
				{label}
			</span>
		</Popovers>
		<Button
			type='button'
			icon={nextIcon}
			isDisable={isDisabled}
			className={nextClassName}
			onClick={onNext}
		/>
	</div>
);

CalendarBox.propTypes = {
	/** 래퍼 div className (기본 'calendar_box') */
	className: PropTypes.string,
	isDisabled: PropTypes.bool,
	/** 가운데 표시할 기간 텍스트 */
	label: PropTypes.node,
	labelClassName: PropTypes.string,
	prevIcon: PropTypes.string,
	nextIcon: PropTypes.string,
	/** ◀ 버튼 className (기본 'btn_prev') */
	prevClassName: PropTypes.string,
	/** ▶ 버튼 className (기본 'btn_next') */
	nextClassName: PropTypes.string,
	onPrev: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
	/** 날짜 선택 팝오버(Popovers) ref */
	// eslint-disable-next-line react/forbid-prop-types
	popRef: PropTypes.object.isRequired,
	popoverId: PropTypes.string,
	/** 팝오버 내용 (DateRange + 부가 UI) - 페이지가 구성 */
	popoverDesc: PropTypes.node,
	popoverClassName: PropTypes.string,
	placement: PropTypes.string,
};

CalendarBox.defaultProps = {
	className: 'calendar_box',
	isDisabled: false,
	label: null,
	labelClassName: 'text-dark txt_date',
	prevIcon: 'ChevronLeft',
	nextIcon: 'ChevronRight',
	prevClassName: 'btn_prev',
	nextClassName: 'btn_next',
	popoverId: 'selectDate',
	popoverDesc: null,
	popoverClassName: 'mw-100',
	placement: 'bottom',
};
